/**
 * Signed-in connection state machine for the cloud (DIME) flow.
 *
 *   sign-in → mint DIME JWT → onboarded?
 *     ├─ no  → 'access-code' → POST /v1/account → resolve connection
 *     └─ yes → GET /v1/connection
 *                ├─ 200 → 'ready' (grant held in memory only)
 *                ├─ 202 → 'provisioning' (poll every 2s)
 *                ├─ 404 → 'access-code'
 *                └─ 409 → 'unavailable'
 *
 * The grant lives exclusively in this hook's state — nothing touches
 * localStorage, so sign-out (which unmounts the signed-in subtree) drops it.
 *
 * `refreshGrant` is the 5-minute-TTL recovery path: the terminal shell calls
 * it when the gateway connection reaches a terminal error (e.g. it tried to
 * handshake with an expired DIME JWT). It re-runs the exchange + connection
 * resolve and bumps `epoch` so the host remounts the terminal with the fresh
 * grant. Rate-limited so a dead gateway can't spin an exchange loop.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ConnectionGrant, DimeCloudClient } from './api.js';
import type { DimeIdentity } from './identity.js';

const PROVISIONING_POLL_MS = 2_000;
const GRANT_REFRESH_MIN_INTERVAL_MS = 15_000;

export type CloudConnectionState =
  | { readonly phase: 'resolving' }
  | {
      readonly phase: 'access-code';
      readonly error: string | null;
      readonly submitting: boolean;
    }
  | { readonly phase: 'provisioning' }
  | {
      readonly phase: 'ready';
      readonly grant: ConnectionGrant;
      /** Bumped on every grant (re-)resolve — remount the terminal on change. */
      readonly epoch: number;
    }
  | { readonly phase: 'unavailable'; readonly status: string }
  | { readonly phase: 'error'; readonly message: string };

export interface CloudConnection {
  readonly state: CloudConnectionState;
  /** Redeem an access code (trimmed here; no other client-side validation). */
  readonly submitAccessCode: (code: string) => void;
  /** Restart the whole flow after an 'error' phase. */
  readonly retry: () => void;
  /**
   * Re-resolve the grant after the gateway connection dies (expired DIME JWT,
   * rotated gateway token/URL). Single-flight + rate-limited; a transient
   * resolve failure leaves the current state untouched.
   */
  readonly refreshGrant: () => void;
}

export function useDimeConnection(client: DimeCloudClient): CloudConnection {
  const [state, setState] = useState<CloudConnectionState>({
    phase: 'resolving',
  });

  // The hook unmounts with the signed-in subtree; guard every async settle.
  const aliveRef = useRef(true);
  // Held in a ref so a host passing an unstable client object doesn't
  // re-bootstrap the flow every render.
  const clientRef = useRef(client);
  clientRef.current = client;
  const pollTimerRef = useRef<number | null>(null);
  const epochRef = useRef(0);
  const inFlightRef = useRef(false);
  const lastRefreshAtRef = useRef(0);

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  /**
   * GET /v1/connection and apply the result; resolves true when a resolution
   * was applied, false on a caught failure (or when skipped/unmounted).
   * `background` = called while the terminal is already up (grant refresh):
   * transport/identity failures then keep the current state instead of tearing
   * the terminal down. `fromPoll` = this call is a provisioning re-poll: a
   * transient failure reschedules the poll (the UI is on the "Setting up…"
   * spinner, which offers no retry). `identity` = a just-minted DIME identity
   * to reuse for the first request (bootstrap / post-redeem) instead of
   * exchanging again; re-polls always mint fresh.
   */
  const resolveConnection = useCallback(
    async (
      background: boolean,
      fromPoll = false,
      identity?: DimeIdentity,
    ): Promise<boolean> => {
      if (inFlightRef.current) return false;
      inFlightRef.current = true;
      const schedulePoll = () => {
        clearPoll();
        pollTimerRef.current = window.setTimeout(() => {
          pollTimerRef.current = null;
          void resolveConnection(background, true);
        }, PROVISIONING_POLL_MS);
      };
      try {
        const resolution = await clientRef.current.fetchConnection(identity);
        if (!aliveRef.current) return false;
        switch (resolution.kind) {
          case 'connected':
            epochRef.current += 1;
            setState({
              phase: 'ready',
              grant: resolution.grant,
              epoch: epochRef.current,
            });
            break;
          case 'provisioning':
            setState({ phase: 'provisioning' });
            schedulePoll();
            break;
          case 'no-account':
            setState({ phase: 'access-code', error: null, submitting: false });
            break;
          case 'unavailable':
            setState({ phase: 'unavailable', status: resolution.status });
            break;
        }
        return true;
      } catch {
        if (!aliveRef.current) return false;
        if (fromPoll) {
          schedulePoll();
          return false;
        }
        if (!background) {
          setState({
            phase: 'error',
            message: 'Could not reach the terminal service.',
          });
        }
        return false;
      } finally {
        inFlightRef.current = false;
      }
    },
    [clearPoll],
  );

  /** Full entry: mint an identity, route on `onboarded`. */
  const bootstrap = useCallback(() => {
    // Remount rehydration: a still-fresh grant from the session cache means
    // this surface was connected moments ago (host chrome remount) — go
    // straight to 'ready' with zero network. Staleness is bounded by the
    // cache's own TTL, and every non-connected resolution invalidates it.
    const cachedGrant = clientRef.current.grantCache.get();
    if (cachedGrant != null) {
      epochRef.current += 1;
      setState({
        phase: 'ready',
        grant: cachedGrant,
        epoch: epochRef.current,
      });
      return;
    }
    setState({ phase: 'resolving' });
    void (async () => {
      let identity: DimeIdentity;
      try {
        identity = await clientRef.current.mintIdentity();
      } catch {
        if (aliveRef.current) {
          setState({
            phase: 'error',
            message: 'Could not reach the terminal service.',
          });
        }
        return;
      }
      if (!aliveRef.current) return;
      if (!identity.onboarded) {
        setState({ phase: 'access-code', error: null, submitting: false });
        return;
      }
      // Reuse the identity just minted for the onboarded check — no second
      // exchange round-trip on startup.
      await resolveConnection(false, false, identity);
    })();
  }, [resolveConnection]);

  useEffect(() => {
    aliveRef.current = true;
    bootstrap();
    return () => {
      aliveRef.current = false;
      clearPoll();
    };
  }, [bootstrap, clearPoll]);

  const submitAccessCode = useCallback(
    (raw: string) => {
      const code = raw.trim();
      if (code.length === 0) return;
      setState((prev) =>
        prev.phase === 'access-code'
          ? { phase: 'access-code', error: null, submitting: true }
          : prev,
      );
      void (async () => {
        try {
          const result = await clientRef.current.redeemAccessCode(code);
          if (!aliveRef.current) return;
          if (result.kind === 'redeemed') {
            setState({ phase: 'resolving' });
            // Reuse the identity that authorized the redeem for the follow-up
            // connection resolve.
            await resolveConnection(false, false, result.identity);
            return;
          }
          setState({
            phase: 'access-code',
            error: result.message,
            submitting: false,
          });
        } catch {
          if (!aliveRef.current) return;
          setState({
            phase: 'access-code',
            error: 'Something went wrong — try again.',
            submitting: false,
          });
        }
      })();
    },
    [resolveConnection],
  );

  const retry = useCallback(() => {
    bootstrap();
  }, [bootstrap]);

  const refreshGrant = useCallback(() => {
    if (inFlightRef.current) return;
    if (Date.now() - lastRefreshAtRef.current < GRANT_REFRESH_MIN_INTERVAL_MS) {
      return;
    }
    // The connection died on this grant — it must not survive in the session
    // cache, or the next remount would rehydrate the same dead grant.
    clientRef.current.grantCache.invalidate();
    void resolveConnection(true).then((applied) => {
      // Start the cooldown only from a refresh that actually resolved —
      // a transient failure (DNS blip, dropped request) shouldn't make the
      // next recovery attempt wait out the interval. Concurrent callers are
      // already serialized by `inFlightRef` while the resolve is in flight.
      if (applied) lastRefreshAtRef.current = Date.now();
    });
  }, [resolveConnection]);

  return { state, submitAccessCode, retry, refreshGrant };
}
